import { InlineRovoPrompt } from '@databases/database-ai-first-creation-ui/InlineRovoPrompt';
import LayoutResizeObserver from '@databases/layout/observer/ResizeObserver';
import {
  useScrollPosition,
  useWindowScrollPosition,
} from '@databases/layout/scroll-position';
import { isNativeEmbed } from '@databases/modes';
import { useResizeObserver } from '@databases/react';
import { type DatabaseEntry } from '@databases/types/database';
import { DatabaseLayoutType } from '@databases/types/database/view';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import useView from '@databases/views/useView';
import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { dynamicImport } from '@canvas/dynamic-import';
import { waitForContentAPI } from '@canvas/frame-api/host-api/host-frame-context';

import { DatabaseBoardSkeleton } from '../database-skeletons/DatabaseBoardSkeleton';
import { DatabaseGallerySkeleton } from '../database-skeletons/DatabaseGallerySkeleton';
import { DatabaseTableSkeletonRefresh } from '../database-skeletons/DatabaseTableSkeletonRefresh';

import { DatabaseTableSkeleton } from './DatabaseContentSkeleton';
import DatabaseContentWrapper from './DatabaseContentWrapper';

/** While scrolling, render the rows in batches of this height, rather than rendering each row on its own. */
const RENDER_BATCH_HEIGHT = 500;
/** Render rows this amount of pixels above and below the visible section as well. */
const RENDER_MARGIN = 800;

const DatabaseContentInnerContainer = styled.div<{ canEditContent: boolean }>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  position: relative;

  // Make sure that the wrapper has the same width as the table inside, to make sure that the scroll position
  // observer elements have 100% width (otherwise the scroll position won't work if we are scrolled way to the right)
  min-width: 100%;
  width: min-content;

  ${({ canEditContent }) =>
    canEditContent &&
    css`
      & tbody > tr:last-child > * {
        border-bottom: none;
      }
    `}
`;

const TableLayout = lazy(() => dynamicImport(() => import('./table-layout/TableLayout')));
const BoardLayout = lazy(() => dynamicImport(() => import('./board-layout/BoardLayout')));
const CalendarLayout = lazy(() =>
  dynamicImport(() => import('./calendar-layout/CalendarLayout'))
);
const GalleryLayout = lazy(() =>
  dynamicImport(() => import('./gallery-layout/GalleryLayout'))
);

const DatabaseLayoutRenderer = ({
  canEditContent,
  couldEditFields,
  canEditFields,
  contentId,
  isContentEditable = false,
  outerContainerRef,
  setOuterContainerRef,
}: {
  contentId: string;
  canEditContent: boolean;
  couldEditFields: boolean;
  canEditFields: boolean;
  isContentEditable?: boolean;
  outerContainerRef: HTMLElement | null;
  setOuterContainerRef: (ref: HTMLElement | null) => void;
}) => {
  const addEntryButtonRef = useRef<HTMLButtonElement>(null);
  const [innerContainerRef, setInnerContainerRef] = useState<HTMLElement | null>(null);
  const [heightIsLessThanContainer, setHeightIsLessThanContainer] = useState(false);
  const outerContainerSize = useResizeObserver(outerContainerRef, false);
  const innerContainerSize = useResizeObserver(innerContainerRef, false);
  const [isConfluencePanelOpen, setIsConfluencePanelOpen] = useState<boolean>(false);
  const isVisualRefreshM1Enabled = isFeatureFlagEnabled(
    'databases-frontend-visual-refresh-table-m1'
  );
  const isDBAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');
  const renderSection = useScrollPosition({
    isDisabled: !innerContainerRef,
    container: innerContainerRef ?? undefined,
    batchHeight: RENDER_BATCH_HEIGHT,
    margin: RENDER_MARGIN,
  });
  const { view } = useView();
  const isNativeEmbedMode = isNativeEmbed();

  useEffect(() => {
    const handleConfluenceSidebarState = async () => {
      const contentAPI = await waitForContentAPI();
      if (contentAPI?.confluenceAPI?.sidebar?.onConfluenceSidebarStateChange) {
        contentAPI.confluenceAPI.sidebar.onConfluenceSidebarStateChange((state) => {
          if (state.isOpen) {
            setIsConfluencePanelOpen(state.isOpen);
          }
        });
      }
    };
    if (isVisualRefreshM1Enabled) {
      handleConfluenceSidebarState();
    }
  }, [isVisualRefreshM1Enabled]);

  // check if innerContainerRef element height is higher than outerContainerRef element height
  useEffect(() => {
    if (outerContainerSize && innerContainerSize) {
      setHeightIsLessThanContainer(
        innerContainerSize.height <= outerContainerSize.height
      );
    }
  }, [outerContainerSize, innerContainerSize]);

  const { left } = useWindowScrollPosition({
    container: innerContainerRef?.parentElement ?? undefined,
  });

  const { top } = useScrollPosition({
    isDisabled: !innerContainerRef,
    container: innerContainerRef ?? undefined,
  });

  // When an entry is newly created, its main field value should be in editable state and focused.
  // TODO: NCTD-3740
  const [editingIds, setEditingIds] = useState<
    Set<{ entryId: string; fieldId: string | undefined }>
  >(new Set());

  const handleEntry = useCallback(
    (entry: DatabaseEntry) => {
      setEditingIds(editingIds.add({ entryId: entry.id, fieldId: undefined }));
      setTimeout(() => {
        if (outerContainerRef && innerContainerRef) {
          outerContainerRef?.scrollTo({
            left: 0,
            top: innerContainerRef?.getBoundingClientRect().height,
            behavior: 'smooth',
          });
        }
      }, 0);
    },
    [editingIds, outerContainerRef, innerContainerRef]
  );

  return (
    <>
      <DatabaseContentWrapper
        innerRef={setOuterContainerRef}
        heightIsLessThanContainer={heightIsLessThanContainer}
        allowOverheadSpace={false}
        hideScrollbar={isNativeEmbedMode}
      >
        <DatabaseContentInnerContainer
          canEditContent={canEditContent}
          id="database-content-inner-container"
          data-testid={'database-content-inner-container'}
          ref={setInnerContainerRef}
        >
          {isDBAiFirstCreationEnabled && outerContainerRef && (
            <InlineRovoPrompt container={outerContainerRef} />
          )}
          <LayoutResizeObserver>
            {view.settings.layout.type === DatabaseLayoutType.GALLERY
              ? outerContainerSize && (
                  <Suspense fallback={<DatabaseGallerySkeleton />}>
                    <GalleryLayout
                      canEditContent={canEditContent}
                      parentContainerRef={innerContainerRef}
                      containerWidth={outerContainerSize.width}
                    />
                  </Suspense>
                )
              : view.settings.layout.type === DatabaseLayoutType.BOARD
              ? outerContainerSize && (
                  <Suspense fallback={<DatabaseBoardSkeleton />}>
                    <BoardLayout
                      canEditFields={canEditFields}
                      canEditContent={canEditContent}
                      containerWidth={outerContainerSize.width}
                      containerRef={innerContainerRef}
                    />
                  </Suspense>
                )
              : view.settings.layout.type === DatabaseLayoutType.CALENDAR
              ? outerContainerSize && (
                  <Suspense fallback={<DatabaseTableSkeleton />}>
                    <CalendarLayout
                      canEditFields={canEditFields}
                      canEditContent={canEditContent}
                      contentId={contentId}
                      renderSection={renderSection}
                      containerWidth={outerContainerRef?.clientWidth ?? 0}
                    />
                  </Suspense>
                )
              : outerContainerSize && (
                  <Suspense fallback={<DatabaseTableSkeletonRefresh />}>
                    <TableLayout
                      editingIds={editingIds}
                      setEditingIds={setEditingIds}
                      contentId={contentId}
                      isContentEditable={isContentEditable}
                      canEditContent={canEditContent}
                      canEditFields={canEditFields}
                      couldEditFields={couldEditFields}
                      renderSection={renderSection}
                      containerWidth={outerContainerRef?.clientWidth ?? 0}
                      addEntryButtonRef={addEntryButtonRef}
                      containerTop={top}
                      containerLeft={left}
                      handleEntry={handleEntry}
                      isConfluencePanelOpen={isConfluencePanelOpen}
                    />
                  </Suspense>
                )}
          </LayoutResizeObserver>
        </DatabaseContentInnerContainer>
      </DatabaseContentWrapper>
    </>
  );
};

export default DatabaseLayoutRenderer;
